import { useRef, useState, type PointerEvent } from "react";
import {
  maximumOrbitDistance,
  minimumOrbitDistance,
} from "../../three/GlbPreview";

type OrientationPreset = {
  angle: number;
  label: string;
};

const orientationPresets: OrientationPreset[] = [
  { angle: 0, label: "正面" },
  { angle: 45, label: "右前" },
  { angle: 90, label: "右侧" },
  { angle: 135, label: "右后" },
  { angle: 180, label: "背面" },
  { angle: 225, label: "左后" },
  { angle: 270, label: "左侧" },
  { angle: 315, label: "左前" },
];

export function ComparisonViewControls({
  angle,
  disabled,
  onAngleChange,
  onReset,
  onZoomIn,
  onZoomOut,
  orbitDistance,
}: {
  angle: number;
  disabled: boolean;
  onAngleChange: (angle: number, animated: boolean) => void;
  onReset: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  orbitDistance: number;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const activePointerIdRef = useRef<number | null>(null);
  const lastPointerAngleRef = useRef(angle);
  const normalizedAngle = normalizeAngle(angle);
  const selectedPreset = nearestPreset(normalizedAngle);
  const knobPosition = pointForAngle(normalizedAngle, 37.5);

  function angleFromPointer(event: PointerEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return angleFromPoint(
      event.clientX - rect.left,
      event.clientY - rect.top,
      rect.width,
      rect.height,
    );
  }

  return (
    <article className="result-card result-control-card">
      <div className="result-card-header compact">
        <div>
          <h3>模型控制</h3>
          <p>调整方位或缩放时，两个 3D 模型会同步变化。</p>
        </div>
      </div>

      <div className={`comparison-view-controls ${disabled ? "disabled" : ""}`}>
        <div
          className="orientation-wheel"
          aria-label="视角控制"
          role="group"
          onPointerDown={(event) => {
            if (disabled) return;
            activePointerIdRef.current = event.pointerId;
            setIsDragging(true);
            event.currentTarget.setPointerCapture(event.pointerId);
            const nextAngle = angleFromPointer(event);
            lastPointerAngleRef.current = nextAngle;
            onAngleChange(nextAngle, false);
          }}
          onPointerMove={(event) => {
            if (
              disabled
              || activePointerIdRef.current !== event.pointerId
              || !event.currentTarget.hasPointerCapture(event.pointerId)
            ) return;
            const nextAngle = angleFromPointer(event);
            lastPointerAngleRef.current = nextAngle;
            onAngleChange(nextAngle, false);
          }}
          onPointerUp={(event) => {
            if (disabled || activePointerIdRef.current !== event.pointerId) return;
            const finalAngle = angleFromPointer(event);
            lastPointerAngleRef.current = finalAngle;
            setIsDragging(false);
            activePointerIdRef.current = null;
            onAngleChange(nearestPreset(finalAngle).angle, true);
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
          }}
          onPointerCancel={(event) => {
            setIsDragging(false);
            activePointerIdRef.current = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
            onAngleChange(nearestPreset(lastPointerAngleRef.current).angle, true);
          }}
          onLostPointerCapture={() => {
            activePointerIdRef.current = null;
            setIsDragging(false);
          }}
        >
          <div className="orientation-wheel-track" aria-hidden="true" />
          {orientationPresets.map((preset) => {
            const position = pointForAngle(preset.angle, 37.5);
            const selected = preset.angle === selectedPreset.angle;
            return (
              <button
                aria-label={`切换到${preset.label}`}
                aria-pressed={selected}
                className={`orientation-wheel-tick ${selected ? "selected" : ""}`}
                disabled={disabled}
                key={preset.angle}
                style={{ left: `${position.x}%`, top: `${position.y}%` }}
                type="button"
                onClick={(event) => {
                  // Pointer input is handled by the wheel so a drag can begin on
                  // any tick. Keep click activation for keyboard/assistive input.
                  if (event.detail === 0) {
                    onAngleChange(preset.angle, true);
                  }
                }}
              />
            );
          })}
          <span
            className={`orientation-wheel-knob ${isDragging ? "dragging" : ""}`}
            aria-hidden="true"
            style={{ left: `${knobPosition.x}%`, top: `${knobPosition.y}%` }}
          />
          <div className="orientation-wheel-center" aria-hidden="true">
            <svg style={{ transform: `rotate(${selectedPreset.angle}deg)` }} viewBox="0 0 24 24">
              <path d="M12 19V5" />
              <path d="m7 10 5-5 5 5" />
            </svg>
            <span>{selectedPreset.label}</span>
          </div>
        </div>

        <div className="orientation-zoom-controls" aria-label="缩放控制" role="group">
          <button
            aria-label="放大"
            disabled={disabled || orbitDistance <= minimumOrbitDistance + 0.001}
            title="放大"
            type="button"
            onClick={onZoomIn}
          >
            <MagnifierIcon kind="plus" />
          </button>
          <button aria-label="复位" disabled={disabled} title="复位" type="button" onClick={onReset}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 10a8 8 0 1 1 2.34 5.66" />
              <path d="M4 4v6h6" />
            </svg>
          </button>
          <button
            aria-label="缩小"
            disabled={disabled || orbitDistance >= maximumOrbitDistance - 0.001}
            title="缩小"
            type="button"
            onClick={onZoomOut}
          >
            <MagnifierIcon kind="minus" />
          </button>
        </div>
      </div>
    </article>
  );
}

function MagnifierIcon({ kind }: { kind: "minus" | "plus" }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 4 4" />
      <path d="M7.5 10.5h6" />
      {kind === "plus" ? <path d="M10.5 7.5v6" /> : null}
    </svg>
  );
}

function pointForAngle(angle: number, radius: number) {
  const radians = normalizeAngle(angle) * Math.PI / 180;
  return {
    x: 50 + Math.sin(radians) * radius,
    y: 50 - Math.cos(radians) * radius,
  };
}

function angleFromPoint(x: number, y: number, width: number, height: number) {
  const degrees = Math.atan2(x - width / 2, -(y - height / 2)) * 180 / Math.PI;
  return normalizeAngle(degrees);
}

function nearestPreset(angle: number) {
  return orientationPresets.reduce((nearest, preset) => (
    angularDistance(preset.angle, angle) < angularDistance(nearest.angle, angle)
      ? preset
      : nearest
  ));
}

function angularDistance(first: number, second: number) {
  const difference = Math.abs(normalizeAngle(first) - normalizeAngle(second));
  return Math.min(difference, 360 - difference);
}

function normalizeAngle(angle: number) {
  const remainder = angle % 360;
  return remainder >= 0 ? remainder : remainder + 360;
}
